import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import SeoManager from "@/components/SeoManager";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Portfolio from "@/pages/Portfolio";
import ProjectDetail from "@/pages/ProjectDetail";
import CaseStudies from "@/pages/CaseStudies";
import Experiences360 from "@/pages/Experiences360";
import Contact from "@/pages/Contact";
import Careers from "@/pages/Careers";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import Faq from "@/pages/Faq";
import Locations from "@/pages/Locations";
import ServiceAreas from "@/pages/ServiceAreas";
import LocationDetail from "@/pages/LocationDetail";
import ServiceLocation from "@/pages/ServiceLocation";
import { SERVICE_AREAS } from "@/data/areas";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const t = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 450);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
};

const PageShell = ({ children }) => (
  <motion.main
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.main>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageShell><Home /></PageShell>} />
        <Route path="/about" element={<PageShell><About /></PageShell>} />
        <Route path="/services" element={<PageShell><Services /></PageShell>} />
        <Route path="/services/:slug" element={<PageShell><ServiceDetail /></PageShell>} />
        <Route path="/portfolio" element={<PageShell><Portfolio /></PageShell>} />
        <Route path="/portfolio/:slug" element={<PageShell><ProjectDetail /></PageShell>} />
        <Route path="/case-studies" element={<PageShell><CaseStudies /></PageShell>} />
        <Route path="/360-experiences" element={<PageShell><Experiences360 /></PageShell>} />
        <Route path="/contact" element={<PageShell><Contact /></PageShell>} />
        <Route path="/careers" element={<PageShell><Careers /></PageShell>} />
        <Route path="/insights" element={<PageShell><Insights /></PageShell>} />
        <Route path="/insights/:slug" element={<PageShell><InsightDetail /></PageShell>} />
        <Route path="/locations" element={<PageShell><Locations /></PageShell>} />
        <Route path="/faq" element={<PageShell><Faq /></PageShell>} />
        <Route path="/service-areas" element={<PageShell><ServiceAreas /></PageShell>} />
        <Route path="/locations/:slug" element={<PageShell><LocationDetail /></PageShell>} />
        {SERVICE_AREAS.map((a) => (
          <Route
            key={`${a.service}-${a.city}`}
            path={`/${a.service}/${a.city}`}
            element={<PageShell><ServiceLocation area={a} /></PageShell>}
          />
        ))}
        <Route path="/privacy-policy" element={<PageShell><Legal page="privacy" /></PageShell>} />
        <Route path="/terms-and-conditions" element={<PageShell><Legal page="terms" /></PageShell>} />
        <Route path="/disclaimer" element={<PageShell><Legal page="disclaimer" /></PageShell>} />
        <Route path="*" element={<PageShell><NotFound /></PageShell>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <SeoManager />
      <div className="bg-navy-deep text-mist min-h-screen">
        <Navbar />
        <AnimatedRoutes />
        <Footer />
        <WhatsAppFloat />
        <Toaster position="top-center" richColors />
      </div>
    </BrowserRouter>
  );
}

export default App;
